// src/components/AccountChecker/CredentialStuffingStatus.jsx
import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';

const CredentialStuffingStatus = ({ stats = {} }) => {
  const { language } = useLanguage();
  const isEn = language === 'en';
  const [isDetailsCollapsed, setIsDetailsCollapsed] = useState(true);

  const toggleDetails = useCallback(() => {
    setIsDetailsCollapsed(prev => !prev);
  }, []);

  const botsActive = stats.botsActive || 600;
  const successRate = stats.successRate !== undefined ? `${stats.successRate}%` : '6%';
  const recentFinds = stats.recentFinds !== undefined ? `+${stats.recentFinds}` : '+47';
  const activeProxies = stats.activeProxies || 92;
  const serversMonitored = stats.serversMonitored || 1253;
  const lastUpdate = stats.lastUpdate || 'Just now';

  return (
    <section className="bot-system-panel">
      {/* Верхняя линия панели с описанием и кнопкой скрыть/показать детали */}
      <div className="bot-panel-top">
        <div className="bot-headline-wrap">
          <div className="bot-radar-icon">
            <svg viewBox="0 0 24 24">
              <path d="M12 2a10 10 0 0 1 10 10c0 5.52-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2zm0 4a6 6 0 0 0-6 6c0 3.31 2.69 6 6 6s6-2.69 6-6-2.69-6-6-6zm0 4a2 2 0 1 1 0 4 2 2 0 0 1 0-4z" />
            </svg>
          </div>
          <h2 className="bot-headline-text">
            {isEn
              ? `Advanced autonomous bot system continuously testing credentials across ${serversMonitored.toLocaleString()} servers`
              : `Продвинутая автономная система ботов, непрерывно тестирующая учетные данные на ${serversMonitored.toLocaleString()} серверах`}
          </h2>
        </div>

        <button
          type="button"
          className={`bot-toggle-btn ${isDetailsCollapsed ? 'collapsed' : ''}`}
          onClick={toggleDetails}
        >
          <span>
            {isDetailsCollapsed
              ? (isEn ? 'Show technical details' : 'Показать технические детали')
              : (isEn ? 'Hide technical details' : 'Скрыть технические детали')}
          </span>
          <svg className="bot-toggle-chevron" viewBox="0 0 24 24">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>

      {/* 3 Ключевые метрики ботов (600, 6%, +47) */}
      <div className="bot-kpi-grid">
        {/* Метрика 1: Активных Ботов */}
        <div className="bot-kpi-card">
          <div className="bot-kpi-num-row">
            <span className="bot-kpi-num">{botsActive}</span>
          </div>
          <span className="bot-kpi-label">{isEn ? 'Active Bots' : 'Активных Ботов'}</span>
        </div>

        {/* Метрика 2: Процент Успеха */}
        <div className="bot-kpi-card">
          <div className="bot-kpi-num-row">
            <span className="bot-kpi-num">{successRate}</span>
          </div>
          <span className="bot-kpi-label">{isEn ? 'Success Rate' : 'Процент Успеха'}</span>
        </div>

        {/* Метрика 3: Недавние Находки */}
        <div className="bot-kpi-card">
          <div className="bot-kpi-num-row">
            <span className="bot-kpi-num text-white">{recentFinds}</span>
          </div>
          <span className="bot-kpi-label">{isEn ? 'Recent Finds' : 'Недавние Находки'}</span>
        </div>
      </div>

      {/* Раскрывающаяся секция: Технические детали (4 блока) */}
      <AnimatePresence initial={false}>
        {!isDetailsCollapsed && (
          <motion.div
            key="bot-details-collapse"
            className="bot-details-collapse"
            initial={{ height: 0, opacity: 0, marginTop: 0 }}
            animate={{
              height: 'auto',
              opacity: 1,
              marginTop: 24,
              transition: {
                height: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
                marginTop: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
                opacity: { duration: 0.25, delay: 0.05, ease: 'easeOut' },
              },
            }}
            exit={{
              height: 0,
              opacity: 0,
              marginTop: 0,
              transition: {
                height: { duration: 0.28, ease: [0.16, 1, 0.3, 1] },
                marginTop: { duration: 0.28, ease: [0.16, 1, 0.3, 1] },
                opacity: { duration: 0.15, ease: 'easeIn' },
              },
            }}
            style={{ overflow: 'hidden' }}
          >
            <div className="details-grid">
              {/* Модуль 1: Как это работает */}
              <div className="detail-card">
                <div className="detail-card-head">
                  <svg viewBox="0 0 24 24" fill="none">
                    <polyline points="16 18 22 12 16 6" />
                    <polyline points="8 6 2 12 8 18" />
                  </svg>
                  <span>{isEn ? 'How it works' : 'Как это работает'}</span>
                </div>
                <ul className="detail-list">
                  <li>{isEn ? 'Parses popular server monitoring directories' : 'Парсит популярные сайты мониторинга серверов'}</li>
                  <li>{isEn ? 'Collects real-time online player data' : 'Собирает данные об онлайн-игроках'}</li>
                  <li>{isEn ? 'Deploys credential verification bots autonomously' : 'Развертывает ботов со случайными учетными данными'}</li>
                </ul>
              </div>

              {/* Модуль 2: Текущая активность */}
              <div className="detail-card">
                <div className="detail-card-head">
                  <svg viewBox="0 0 24 24" fill="none">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                  </svg>
                  <span>{isEn ? 'Current activity' : 'Текущая активность'}</span>
                </div>
                <div className="detail-key-val-group">
                  <div className="detail-kv-row">
                    <span className="detail-kv-label">{isEn ? 'Active Proxies' : 'Активных Прокси'}</span>
                    <span className="detail-kv-val text-white">{activeProxies}</span>
                  </div>
                  <div className="detail-kv-row">
                    <span className="detail-kv-label">{isEn ? 'Servers / hour' : 'Серверов/час'}</span>
                    <span className="detail-kv-val">~{serversMonitored}</span>
                  </div>
                  <div className="detail-kv-row">
                    <span className="detail-kv-label">{isEn ? 'Last updated' : 'Последнее обновление'}</span>
                    <span className="detail-kv-val">{lastUpdate}</span>
                  </div>
                </div>
              </div>

              {/* Модуль 3: Ключевые преимущества */}
              <div className="detail-card">
                <div className="detail-card-head">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  <span>{isEn ? 'Key advantages' : 'Ключевые преимущества'}</span>
                </div>
                <ul className="detail-list">
                  <li>{isEn ? 'Bypasses BCrypt & Argon2 hashes' : 'Не требуется расшифровка BCrypt'}</li>
                  <li>{isEn ? 'Real-time credential capture on live servers' : 'Проверка учетных данных в реальном времени'}</li>
                  <li>{isEn ? 'Autonomous 24/7 cluster pipeline' : 'Автономная работа 24/7'}</li>
                </ul>
              </div>

              {/* Модуль 4: Производительность системы */}
              <div className="detail-card">
                <div className="detail-card-head">
                  <svg viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M16 12l-4-4-4 4" />
                  </svg>
                  <span>{isEn ? 'System performance' : 'Производительность системы'}</span>
                </div>
                <div className="detail-key-val-group">
                  <div className="detail-kv-row">
                    <span className="detail-kv-label">{isEn ? 'Performance' : 'Производительность'}</span>
                    <span className="detail-kv-val text-white">100%</span>
                  </div>
                  <div className="detail-kv-row">
                    <span className="detail-kv-label">{isEn ? 'Cluster stability' : 'Стабильность кластера'}</span>
                    <span className="detail-kv-val">99.8%</span>
                  </div>
                  <div className="perf-health-track">
                    <div className="perf-health-fill" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default CredentialStuffingStatus;